/**
 * Pure utility functions for highlight overlay positioning.
 * Extracted from HighlightHandler.tsx so they can be unit tested
 * without React/Cloudscape dependencies.
 */

export interface HighlightRect {
    top: number;
    left: number;
    width: number;
    height: number;
}

/**
 * Merges highlight rects that overlap vertically (within 50% height) and horizontally.
 * Sorts by top then left before merging. Returns a new array (no mutation).
 */
export function mergeOverlappingRects(rects: HighlightRect[]): HighlightRect[] {
    if (rects.length === 0) return [];
    const sorted = [...rects].sort((a, b) => a.top - b.top || a.left - b.left);
    const merged: HighlightRect[] = [{ ...sorted[0] }];

    for (let i = 1; i < sorted.length; i++) {
        const current = sorted[i];
        const last = merged[merged.length - 1];
        const verticalOverlap = current.top < last.top + last.height * 0.5;
        const horizontalOverlap = current.left < last.left + last.width;

        if (verticalOverlap && horizontalOverlap) {
            const right = Math.max(last.left + last.width, current.left + current.width);
            const bottom = Math.max(last.top + last.height, current.top + current.height);
            last.left = Math.min(last.left, current.left);
            last.top = Math.min(last.top, current.top);
            last.width = right - last.left;
            last.height = bottom - last.top;
        } else {
            merged.push({ ...current });
        }
    }
    return merged;
}

/**
 * Filters zero-size rects, adds scroll offset, then merges overlapping rects.
 * Useful for testing the rect pipeline without a real DOM Range.
 */
export function processRawClientRects(
    rects: Array<{ top: number; left: number; width: number; height: number }>,
    scrollX: number,
    scrollY: number
): HighlightRect[] {
    const valid: HighlightRect[] = [];
    for (const r of rects) {
        if (r.width > 0 && r.height > 0) {
            valid.push({
                top: r.top + scrollY,
                left: r.left + scrollX,
                width: r.width,
                height: r.height
            });
        }
    }
    return mergeOverlappingRects(valid);
}

/**
 * Shallow equality check for two HighlightRect arrays. Returns true if both
 * arrays have the same length and every rect has identical top/left/width/height.
 * Used as the re-entrancy guard in updateOverlayPositions: if positions haven't
 * changed, React gets the same reference back, skips re-render, and breaks the
 * observer→setState→DOM→observer cycle.
 *
 * Uses Object.is() instead of strict equality so NaN coordinates (plausible
 * during accordion-animation layout thrash or from degenerate merged ranges)
 * compare as equal, preventing the guard from reporting "not equal" forever
 * and causing an infinite observer→setState→DOM loop.
 */
export function shallowRectsEqual(prev: HighlightRect[], next: HighlightRect[]): boolean {
    return prev.length === next.length && prev.every((r, i) =>
        Object.is(r.top, next[i].top) && Object.is(r.left, next[i].left) &&
        Object.is(r.width, next[i].width) && Object.is(r.height, next[i].height)
    );
}

/**
 * Resolves the MutationObserver target by walking up the ancestor chain from
 * the highlight container. Prefers .page-layout-main (catches sibling accordion
 * mutations), falls back through .sherpa-main-ui → parentElement → container.
 *
 * Guards against non-Element inputs (e.g. a Document or host object without
 * .closest) that would throw TypeError from the ancestor walk.
 */
export function resolveObserveTarget(container: Element): Element {
    if (typeof container.closest !== "function") return container;
    return container.closest(".page-layout-main")
        || container.closest(".sherpa-main-ui")
        || container.parentElement
        || container;
}

/**
 * Resolves highlight-related URL params from a query parameter map.
 * Returns null for each absent param. Pure function — no DOM access.
 * Used by the hashchange handler and initial state to keep URL parsing testable.
 */
export interface HighlightParams {
    highlight: string | null;
    container: string | null;
    highlightContext: string | null;
}

export function resolveHighlightParams(params: Map<string, string>): HighlightParams {
    return {
        highlight: params.get("highlight") || null,
        container: params.get("container") || null,
        highlightContext: params.get("highlightContext") || null,
    };
}

/** Min overlay shift (px) that triggers a re-scroll after layout changes.
 *  Lowered from 200→80 because accordion expand/collapse can shift content
 *  by 100-180px, which previously wouldn't trigger re-scroll. */
export const RE_SCROLL_THRESHOLD_PX = 80;

/** Whether the overlay shifted enough from its initial position to warrant a re-scroll. */
export function shouldReScroll(initialTop: number, currentTop: number, threshold: number): boolean {
    return Math.abs(currentTop - initialTop) > threshold;
}
